import { inGroupsOf } from 'utils/array';

export type TaskFactory<TResult> = () => Promise<TResult>;

/**
 * Executes all tasks in order.  Will reject if any task fails.
 *
 * @param tasks Array of task callbacks.
 * @returns The results of the tasks.
 */
export function promiseAllSync<TResult>(
    tasks: TaskFactory<TResult>[],
): Promise<TResult[]> {
    return tasks.reduce<Promise<TResult[]>>(
        (prevResults, task) =>
            prevResults.then((results) =>
                task().then((taskResult) => [...results, taskResult]),
            ),
        Promise.resolve<TResult[]>([]),
    );
}

/**
 * Executes all tasks in batches.  Will reject if any task fails.
 *
 * @param tasks Array of task callbacks.
 * @param batchSize The number of tasks to execute at once.
 * @returns The results of the tasks.
 */
export async function promiseAllBatched<TResult>(
    tasks: TaskFactory<TResult>[],
    batchSize: number,
): Promise<TResult[]> {
    const taskBatches = inGroupsOf(tasks, batchSize).map(
        (group) => () => Promise.all(group.map((fn) => fn())),
    );

    return (await promiseAllSync(taskBatches)).flatMap((res) => res);
}

export type DeferredState = 'pending' | 'fulfilled' | 'rejected';

export type Deferred<TResult> = Readonly<{
    resolve: (value: TResult | PromiseLike<TResult>) => Promise<void>;
    reject: (reason?: unknown) => Promise<void>;
    promise: Promise<TResult>;
    getState: () => DeferredState;
}>;

/**
 * Utility used to make testing promises easier.
 * Returns an object with a promise and methods to
 * resolve or reject a value on the promise.
 *
 * @returns a deferred object.
 *
 * @example
 *
 * const deferred = defer<number>();
 * console.log(deferred.getState()); // "pending"
 * deferred.promise.then((result) => console.log(result)); // will print "1"
 * await deferred.resolve(1);
 * console.log(deferred.getState()); // "fulfilled"
 *
 * @example
 *
 * const deferred = defer<number>();
 * console.log(deferred.getState()); // "pending"
 * deferred.promise.catch((result) => console.log(result)); // will print "error"
 * await deferred.reject('error');
 * console.log(deferred.getState()); // "rejected"
 */
export function defer<TResult>(): Deferred<TResult> {
    let state: DeferredState = 'pending';
    let resolve: Deferred<TResult>['resolve'] | undefined;
    let reject: Deferred<TResult>['reject'] | undefined;

    const promise = new Promise<TResult>((res, rej) => {
        resolve = async (value) => {
            if (state !== 'pending') {
                throw new Error(`Deferred is already ${state}`);
            }
            state = 'fulfilled';
            res(value);
            await promise.then(() => Promise.resolve());
        };
        reject = async (reason) => {
            if (state !== 'pending') {
                throw new Error(`Deferred is already ${state}`);
            }
            state = 'rejected';
            rej(reason);
            await promise.catch(() => Promise.resolve());
        };
    });

    if (resolve === undefined || reject === undefined) {
        throw new Error('Unexpected Promise implementation');
    }

    return {
        resolve,
        reject,
        promise,
        getState: () => state,
    };
}
